import ApiClient from './ApiClient.js';
import { STORAGE, storageGet, storageSet } from '../utils/storageKeys.js';

/**
 * Account service. Every visitor silently becomes a guest account, restored
 * on each visit via a persistent deviceId — until they validate the account
 * (username/email/password) or log into an existing one.
 * Fully non-blocking: offline, the game keeps working on localStorage and
 * `this.user` stays null.
 */
export default class AuthService {
  constructor() {
    this.api = new ApiClient();
    this.user = null;
    this.onChange = null; // (user|null) => void, wired by the UI
    this._otpChallenge = null;
    this._bootPromise = null;
    this._initPromise = null;
  }

  get isOnline() {
    return this.user !== null;
  }

  _deviceId() {
    let id = storageGet(STORAGE.DEVICE_ID);
    if (!id) {
      id = crypto.randomUUID();
      storageSet(STORAGE.DEVICE_ID, id);
    }
    return id;
  }

  _setUser(user) {
    this.user = user;
    this.onChange?.(user);
  }

  /** Silent login: existing token first, otherwise guest by deviceId. */
  async init() {
    if (this._initPromise) return this._initPromise;
    this._initPromise = this._init();
    try {
      return await this._initPromise;
    } finally {
      this._initPromise = null;
    }
  }

  async _init() {
    if (this.api.token) {
      const me = await this.api.get('/auth/me');
      if (me.ok && me.data?.user) {
        this._setUser(me.data.user);
        return this.user;
      }
      if (me.status === 401 || me.status === 403 || me.status === 404) {
        this.api.setToken(null); // stale token: fall through to guest
      } else if (me.status === 0) {
        return null; // offline: keep the token for next time
      }
    }
    return this._guest();
  }

  /** Resolves when silent guest/login has finished (or failed offline). */
  async ready() {
    if (this._bootPromise) await this._bootPromise;
    if (!this.user) await this.init();
    return this.user;
  }

  async _guest() {
    const res = await this.api.post('/auth/guest', { deviceId: this._deviceId() });
    if (res.ok && res.data?.token) {
      this.api.setToken(res.data.token);
      this._setUser(res.data.user);
      return this.user;
    }
    return null;
  }

  /** Consume magic / checkout query params before the silent guest restore. */
  bootFromUrl() {
    if (!this._bootPromise) this._bootPromise = this._bootFromUrl();
    return this._bootPromise;
  }

  async _bootFromUrl() {
    const params = new URLSearchParams(location.search);
    const magic = params.get('magic');
    const orbit = params.get('orbit');
    const sessionId = params.get('session_id');
    let note = null;
    if (magic) {
      const result = await this.consumeMagic(magic);
      note = result.ok ? { tone: 'ok', text: 'Connecté via le lien magique.' } : { tone: 'err', text: result.message };
    }
    if (!this.user) await this.init();
    if (orbit === 'ok' && sessionId) {
      const verified = await this.verifyCheckout(sessionId);
      note = verified.isPremium
        ? { tone: 'ok', text: 'Orbit Pass activé. Le coffre est ouvert.' }
        : { tone: 'err', text: verified.message || 'Paiement en attente.' };
    } else if (orbit === 'cancel') {
      note = { tone: 'err', text: 'Paiement annulé.' };
    }
    if (magic || orbit) {
      history.replaceState({}, '', location.pathname || '/');
    }
    return note;
  }

  /** Start an anti-cheat play session before a song begins. */
  async beginSession(songId, difficulty, mode = 'play') {
    if (!this.isOnline) return null;
    const res = await this.api.post('/game/session/begin', { songId, difficulty, mode });
    if (res.ok) return res.data;
    console.warn('[orbeat] session/begin failed', res.status, res.data);
    return { error: res.data?.code || res.data?.message };
  }

  /** Guest -> named account. Returns { ok, message }. */
  async convert({ username, email, password }) {
    const res = await this.api.post('/auth/convert', { username, email, password });
    if (res.ok && res.data?.token) {
      this.api.setToken(res.data.token);
      this._setUser(res.data.user);
      return { ok: true };
    }
    return { ok: false, message: res.data?.message || 'Serveur injoignable — réessaie plus tard.' };
  }

  /** Step 1: password. Returns { ok, needsOtp, challengeId, emailHint }. */
  async login({ username, password }) {
    const res = await this.api.post('/auth/login', { username, password });
    if (res.ok && res.data?.needsOtp) {
      this._otpChallenge = res.data.challengeId;
      return { ok: true, needsOtp: true, emailHint: res.data.emailHint };
    }
    if (res.ok && res.data?.token) {
      this.api.setToken(res.data.token);
      this._setUser(res.data.user);
      return { ok: true };
    }
    return { ok: false, message: res.data?.message || 'Serveur injoignable — réessaie plus tard.' };
  }

  async verifyOtp(code) {
    const res = await this.api.post('/auth/login/verify', {
      challengeId: this._otpChallenge,
      code: String(code || '').replace(/\s+/g, '')
    });
    if (res.ok && res.data?.token) {
      this._otpChallenge = null;
      this.api.setToken(res.data.token);
      this._setUser(res.data.user);
      return { ok: true };
    }
    return { ok: false, message: res.data?.message || 'Code invalide.' };
  }

  async requestMagic(email) {
    const res = await this.api.post('/auth/magic', { email });
    return {
      ok: res.ok,
      message: res.data?.message || (res.ok ? 'Si un compte existe, un lien part maintenant.' : 'Envoi impossible.')
    };
  }

  async consumeMagic(token) {
    const res = await this.api.post('/auth/magic/consume', { token });
    if (res.ok && res.data?.token) {
      this.api.setToken(res.data.token);
      this._setUser(res.data.user);
      return { ok: true };
    }
    return { ok: false, message: res.data?.message || 'Lien expiré ou déjà utilisé.' };
  }

  async startCheckout(interval = 'month') {
    const res = await this.api.post('/economy/checkout', { interval });
    if (res.ok && res.data?.url) {
      window.location.href = res.data.url;
      return { ok: true };
    }
    return { ok: false, message: res.data?.message || 'Paiement indisponible.' };
  }

  async verifyCheckout(sessionId) {
    const res = await this.api.post('/economy/verify-session', { sessionId });
    if (res.ok && res.data?.isPremium && this.user) {
      this._setUser({ ...this.user, isPremium: true, premiumUntil: res.data.premiumUntil || this.user.premiumUntil });
    }
    return { ok: res.ok, isPremium: !!res.data?.isPremium, message: res.data?.message };
  }

  async openPortal() {
    const res = await this.api.post('/economy/portal');
    if (res.ok && res.data?.url) {
      window.location.href = res.data.url;
      return { ok: true };
    }
    return { ok: false, message: res.data?.message || 'Portail indisponible.' };
  }

  /** Logout: drops the token and falls back to a (new or restored) guest. */
  async logout() {
    this.api.setToken(null);
    this._setUser(null);
    return this._guest();
  }

  async resendVerification() {
    const res = await this.api.post('/auth/resend-verification');
    return { ok: res.ok, message: res.data?.message };
  }

  /** Submit a completed run — requires the sessionToken from beginSession. */
  async submitScore({ songId, difficulty, score, maxCombo, grade, sessionToken }) {
    if (!this.isOnline || !sessionToken) {
      console.warn('[orbeat] score not sent', { online: this.isOnline, hasSession: !!sessionToken });
      return { ok: false };
    }
    const res = await this.api.post('/scores', {
      songId,
      difficulty,
      score,
      maxCombo,
      grade,
      sessionToken
    });
    if (!res.ok) {
      console.warn('[orbeat] score rejected', res.status, res.data);
    }
    return { ok: res.ok, improved: res.data?.improved ?? false, rank: res.data?.rank ?? null };
  }

  /** All my best scores (one per song/difficulty), for the catalog badges. */
  async getMyScores() {
    if (!this.isOnline) return null;
    const res = await this.api.get('/scores/me');
    return res.ok ? (res.data?.scores ?? []) : null;
  }

  async getLeaderboard(songId, difficulty) {
    const res = await this.api.get(`/scores/leaderboard/${encodeURIComponent(songId)}?difficulty=${encodeURIComponent(difficulty)}`);
    return res.ok ? res.data : null;
  }

  /** Publish a finished run's universe to the shared gallery (guests too). */
  async submitUniverse({ universe, songId, score }) {
    if (!this.isOnline || !universe) return { ok: false };
    const res = await this.api.post('/universes', { universe, songId, score });
    return { ok: res.ok, id: res.data?.id ?? null };
  }

  /** Shared gallery: sort = 'top' (like ranking) | 'recent' | 'mine'. */
  async getUniverses(sort = 'top') {
    const res = await this.api.get(`/universes?sort=${encodeURIComponent(sort)}`);
    return res.ok ? (res.data?.universes ?? []) : null;
  }

  /** Toggle my like on a universe. Returns { liked, likes } or null offline. */
  async toggleUniverseLike(id) {
    if (!this.isOnline) return null;
    const res = await this.api.post(`/universes/${encodeURIComponent(id)}/like`);
    return res.ok ? res.data : null;
  }
}
